// src/ConsumerApp/Screens/Account/ProfileContent.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../assets/theme/theme';

// Real SVGs from assets/icons
import PencilSimpleLine from '../../../assets/icons/PencilSimpleLine1.svg';
import SealCheck from '../../../assets/icons/profileStatus/SealCheck.svg';
import ShieldCheck from '../../../assets/icons/profileStatus/ShieldCheck.svg';
import CheckCircle from '../../../assets/icons/CheckCircle.svg';
import CameraIcon from '../../../assets/icons/CameraRed.svg';
import X from '../../../assets/icons/X.svg';

// Custom API Hooks
import {
  useGetProfileDetails,
  useUpdateProfileDetails,
  useDeleteCustomerAccount,
} from '../../api/profile/profile.hooks';

const BUYER_TYPES = [
  { id: 'Individual', title: 'Individual', subtitle: 'Personal renovations' },
  { id: 'Home Builder', title: 'Home Builder', subtitle: 'Building my own home' },
  { id: 'Pro Builder', title: 'Pro Builder', subtitle: 'Construction professional' },
];

export default function ProfileContent() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // 1. Session Context
  const storedUser = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = storedUser?._id || storedUser?.id;
  const token = storedUser?.token || sessionStorage.getItem('@auth_token') || localStorage.getItem('@auth_token');

  // 2. React Query Hooks
  const { data: profileData, isLoading: loading } = useGetProfileDetails(customerId, token);
  const { mutate: updateProfile, isPending: isSaving } = useUpdateProfileDetails(token);
  const { mutate: deleteAccount, isPending: isDeletingAccount } = useDeleteCustomerAccount(token);

  // 3. Section Edit States
  const [isPersonalEditing, setIsPersonalEditing] = useState(false);
  const [isBusinessEditing, setIsBusinessEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  // 4. Form Field States
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [selectedBuyerType, setSelectedBuyerType] = useState('Pro Builder');
  const [companyName, setCompanyName] = useState('');
  const [typeOfFirm, setTypeOfFirm] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [association, setAssociation] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // 5. Populate Form on Load
  useEffect(() => {
    if (profileData) {
      setName(profileData.customer_name || profileData.name || '');
      setMobile(profileData.mobile_number || profileData.phone || '');
      setEmail(profileData.email || '');
      setSelectedBuyerType(profileData.customer_type || profileData.buyer_type || 'Pro Builder');
      setCompanyName(profileData.company_name || '');
      setTypeOfFirm(profileData.type_of_firm || '');
      setGstin(profileData.gstin ? profileData.gstin.trim() : '');
      setPan(profileData.pan || '');
      setAssociation(profileData.association || '');

      const img = profileData.image || profileData.files?.[0]?.url || profileData.profile_image;
      if (img) {
        setImagePreview(img.startsWith('http') ? img : `https://assets.brickbucket.in/${img}`);
      }

      setIsPersonalEditing(false);
      setIsBusinessEditing(false);
    }
  }, [profileData]);

  // 6. Dirty Check
  const isFormDirty = useMemo(() => {
    if (!profileData) return false;
    return (
      name !== (profileData.customer_name || profileData.name || '') ||
      mobile !== (profileData.mobile_number || profileData.phone || '') ||
      email !== (profileData.email || '') ||
      selectedBuyerType !== (profileData.customer_type || profileData.buyer_type || 'Pro Builder') ||
      companyName !== (profileData.company_name || '') ||
      gstin !== (profileData.gstin ? profileData.gstin.trim() : '') ||
      pan !== (profileData.pan || '') ||
      imageFile !== null
    );
  }, [name, mobile, email, selectedBuyerType, companyName, gstin, pan, imageFile, profileData]);

  // Handlers
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Name cannot be empty.');
      return;
    }

    const payload = {
      id: customerId,
      name,
      mobile,
      email,
      selectedBuyerType,
      companyName,
      typeOfFirm,
      gstin,
      pan,
      association,
      imageFile,
    };

    updateProfile(payload, {
      onSuccess: () => {
        alert('Profile updated successfully!');
        setIsPersonalEditing(false);
        setIsBusinessEditing(false);
        setImageFile(null);
      },
      onError: (err) => {
        alert(err.message || 'Update failed.');
      },
    });
  };

  const handleDeleteConfirm = () => {
    deleteAccount(
      { customer_id: customerId, feedback: feedbackText.trim() },
      {
        onSuccess: () => {
          setShowDeleteModal(false);
          sessionStorage.clear();
          localStorage.clear();
          navigate('/consumer/signin');
        },
        onError: (err) => {
          alert(err.message || 'Failed to delete account.');
        },
      }
    );
  };

  if (loading) {
    return (
      <div style={styles.loadingBox}>
        <span>Loading profile details...</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ── Header Row ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>My Profile</h2>
          <span style={styles.headerSubtitle}>Manage your account information and preferences</span>
        </div>
      </div>

      {/* ── Status Badges ── */}
      <div style={styles.badgesRow}>
        <div style={{ ...styles.badge, ...styles.successBadge }}>
          <img src={SealCheck} alt="verified" style={{ width: 13, height: 13 }} />
          <span>Phone verified</span>
        </div>
        <div style={{ ...styles.badge, ...styles.successBadge }}>
          <img src={SealCheck} alt="verified" style={{ width: 13, height: 13 }} />
          <span>Email verified</span>
        </div>
        <div style={{ ...styles.badge, ...styles.warningBadge }}>
          <img src={ShieldCheck} alt="pending" style={{ width: 13, height: 13 }} />
          <span>GSTIN pending</span>
        </div>
      </div>

      {/* ── Section 1: Personal Details ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.sectionTitle}>Personal Details</h3>
          <button
            type="button"
            style={styles.editToggleBtn}
            onClick={() => setIsPersonalEditing(!isPersonalEditing)}
          >
            {!isPersonalEditing && <img src={PencilSimpleLine} alt="edit" style={{ width: 13, height: 13 }} />}
            <span>{isPersonalEditing ? 'Cancel' : 'Edit'}</span>
          </button>
        </div>

        {isPersonalEditing ? (
          <div style={styles.formStack}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Full Name</label>
              <input
                type="text"
                style={styles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter full name"
              />
            </div>

            <div style={styles.twoColRow}>
              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>Mobile Number</label>
                <input
                  type="text"
                  style={styles.input}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="Enter mobile number"
                />
              </div>

              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  style={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
            </div>

            {/* Photo Upload Box */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Profile Photo (Optional)</label>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handlePhotoSelect}
              />
              <div
                style={styles.uploadArea}
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div style={styles.previewWrap}>
                    <img src={imagePreview} alt="avatar" style={styles.previewAvatar} />
                    <span style={styles.changePhotoText}>Click to replace photo</span>
                  </div>
                ) : (
                  <div style={styles.uploadPrompt}>
                    <img src={CameraIcon} alt="camera" style={{ width: 28, height: 28 }} />
                    <span style={styles.uploadText}>Click to upload JPG / PNG (Max 5 MB)</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.readOnlyGrid}>
            <div style={styles.displayField}>
              <span style={styles.displayLabel}>Full Name</span>
              <span style={styles.displayValue}>{name || 'Not provided'}</span>
            </div>
            <div style={styles.displayField}>
              <span style={styles.displayLabel}>Mobile Number</span>
              <span style={styles.displayValue}>{mobile || 'Not provided'}</span>
            </div>
            <div style={styles.displayField}>
              <span style={styles.displayLabel}>Email</span>
              <span style={styles.displayValue}>{email || 'Not provided'}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Section 2: Buyer Type ── */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Buyer Type</h3>
        <span style={styles.sectionHelpText}>Select your role for procurement pricing and tax invoices</span>

        <div style={styles.buyerTypesGrid}>
          {BUYER_TYPES.map((type) => {
            const isSelected = selectedBuyerType?.toLowerCase() === type.id.toLowerCase();
            return (
              <div
                key={type.id}
                style={{
                  ...styles.buyerTypeCard,
                  ...(isSelected ? styles.buyerTypeCardSelected : {}),
                }}
                onClick={() => setSelectedBuyerType(type.id)}
              >
                <div style={styles.buyerTypeHeader}>
                  <strong style={styles.buyerTypeTitle}>{type.title}</strong>
                  {isSelected && <img src={CheckCircle} alt="selected" style={{ width: 16, height: 16 }} />}
                </div>
                <span style={styles.buyerTypeSubtitle}>{type.subtitle}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Section 3: Business Details ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.sectionTitle}>Business Details</h3>
          <button
            type="button"
            style={styles.editToggleBtn}
            onClick={() => setIsBusinessEditing(!isBusinessEditing)}
          >
            {!isBusinessEditing && <img src={PencilSimpleLine} alt="edit" style={{ width: 13, height: 13 }} />}
            <span>{isBusinessEditing ? 'Cancel' : 'Edit'}</span>
          </button>
        </div>

        {isBusinessEditing ? (
          <div style={styles.formStack}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Company Name</label>
              <input
                type="text"
                style={styles.input}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. Greenfield Builders Pvt Ltd"
              />
            </div>

            <div style={styles.twoColRow}>
              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>GSTIN</label>
                <input
                  type="text"
                  style={{ ...styles.input, textTransform: 'uppercase' }}
                  value={gstin}
                  onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  placeholder="22AAAAA0000A1Z5"
                />
              </div>

              <div style={{ ...styles.fieldGroup, flex: 1 }}>
                <label style={styles.label}>PAN</label>
                <input
                  type="text"
                  style={{ ...styles.input, textTransform: 'uppercase' }}
                  value={pan}
                  onChange={(e) => setPan(e.target.value.toUpperCase())}
                  placeholder="ABCDE1234F"
                />
              </div>
            </div>
          </div>
        ) : (
          <div style={styles.readOnlyGrid}>
            <div style={styles.displayField}>
              <span style={styles.displayLabel}>Company Name</span>
              <span style={styles.displayValue}>{companyName || 'Not provided'}</span>
            </div>
            <div style={styles.displayField}>
              <span style={styles.displayLabel}>GSTIN</span>
              <span style={styles.displayValue}>{gstin || 'Not provided'}</span>
            </div>
            <div style={styles.displayField}>
              <span style={styles.displayLabel}>PAN</span>
              <span style={styles.displayValue}>{pan || 'Not provided'}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Save Action Bar ── */}
      <div style={styles.saveActionRow}>
        <button
          type="button"
          style={{
            ...styles.saveBtn,
            opacity: !isFormDirty || isSaving ? 0.5 : 1,
            cursor: !isFormDirty || isSaving ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSave}
          disabled={!isFormDirty || isSaving}
        >
          {isSaving ? 'Saving changes...' : 'Save changes'}
        </button>
      </div>

      {/* ── Danger Zone: Delete Account ── */}
      <div style={styles.dangerZone}>
        <button
          type="button"
          style={styles.deleteAccountLink}
          onClick={() => setShowDeleteModal(true)}
        >
          Delete Account
        </button>
        <span style={styles.deleteSubtext}>
          Permanently remove your profile, open orders, and billing records
        </span>
      </div>

      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <div style={styles.modalOverlay} onClick={() => !isDeletingAccount && setShowDeleteModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h4 style={styles.modalTitle}>Delete Procurement Account</h4>
              <button
                type="button"
                style={styles.modalCloseBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                <img src={X} alt="close" style={{ width: 14, height: 14 }} />
              </button>
            </div>

            <p style={styles.modalWarning}>
              Deleting your account is permanent. You will immediately lose access to open quotes,
              material orders, RFQs, and GST billing records.
            </p>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Anything we could have done better? (Optional)</label>
              <textarea
                style={styles.textarea}
                rows={3}
                placeholder="Share your thoughts with us..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                maxLength={300}
                disabled={isDeletingAccount}
              />
              <span style={styles.charCount}>{feedbackText.length}/300</span>
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.modalCancelBtn}
                disabled={isDeletingAccount}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={styles.modalDeleteBtn}
                disabled={isDeletingAccount}
                onClick={handleDeleteConfirm}
              >
                {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  loadingBox: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 14,
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  badgesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: '600',
  },
  successBadge: {
    backgroundColor: '#E8F5EE',
    color: '#1E8449',
  },
  warningBadge: {
    backgroundColor: '#FFF8EB',
    color: '#C47A00',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: '700',
    color: '#2B2A29',
  },
  sectionHelpText: {
    fontSize: 12,
    color: '#707B8C',
    marginTop: -8,
  },
  editToggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    background: 'none',
    border: 'none',
    color: '#C7061F',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    padding: 0,
  },
  formStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  twoColRow: {
    display: 'flex',
    gap: 16,
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    position: 'relative',
  },
  label: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#2B2A29',
  },
  input: {
    height: 42,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    padding: '0 14px',
    fontSize: 13.5,
    color: '#2B2A29',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
  },
  uploadArea: {
    minHeight: 110,
    border: '1.5px dashed #CBD5E1',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: '#FAFAFA',
    padding: 16,
  },
  uploadPrompt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  uploadText: {
    fontSize: 12,
    color: '#707B8C',
  },
  previewWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
  },
  previewAvatar: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    objectFit: 'cover',
  },
  changePhotoText: {
    fontSize: 11.5,
    fontWeight: '600',
    color: '#C7061F',
  },
  readOnlyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 16,
  },
  displayField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  displayLabel: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  displayValue: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#2B2A29',
  },
  buyerTypesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  buyerTypeCard: {
    border: '1px solid #CBD5E1',
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    transition: 'all 0.15s ease',
    backgroundColor: '#FFFFFF',
  },
  buyerTypeCardSelected: {
    borderColor: '#C7061F',
    backgroundColor: '#FFF8F8',
    boxShadow: '0 0 0 1px #C7061F',
  },
  buyerTypeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  buyerTypeTitle: {
    fontSize: 13.5,
    color: '#2B2A29',
  },
  buyerTypeSubtitle: {
    fontSize: 11,
    color: '#707B8C',
  },
  saveActionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  saveBtn: {
    backgroundColor: '#C7061F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    padding: '12px 28px',
    fontSize: 14,
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
  },
  dangerZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    paddingTop: 12,
  },
  deleteAccountLink: {
    background: 'none',
    border: 'none',
    color: '#DC2626',
    fontSize: 13.5,
    fontWeight: '700',
    cursor: 'pointer',
    padding: 0,
  },
  deleteSubtext: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(2px)',
    padding: 20,
  },
  modalCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: '24px 28px',
    width: '100%',
    maxWidth: 440,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: '700',
    color: '#2B2A29',
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
  },
  modalWarning: {
    margin: 0,
    fontSize: 13,
    color: '#636C78',
    lineHeight: '19px',
  },
  textarea: {
    width: '100%',
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    padding: '10px 12px',
    fontSize: 13,
    color: '#2B2A29',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
  },
  charCount: {
    fontSize: 11,
    color: '#707B8C',
    alignSelf: 'flex-end',
    marginTop: 2,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 4,
  },
  modalCancelBtn: {
    padding: '10px 18px',
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#2B2A29',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
  },
  modalDeleteBtn: {
    padding: '10px 18px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: '#DC2626',
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
};